import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { validateEmail, validatePassword } from '../../utils/validation';
import { isAuthError, getAuthErrorMessage } from '../../utils/auth/errors';
import type { AuthResponse } from '@supabase/supabase-js';

const ATTEMPT_KEY = 'tezhly_login_attempts';
const LOCKOUT_KEY = 'tezhly_login_lockout';
const RETRY_DELAY = 1000; // Base delay of 1s with exponential backoff
const MAX_RETRIES = 3;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
const DB_ERROR_CODES = [
  'unexpected_failure',
  'Database error granting user',
  'Connection error',
  'Network error',
  'Failed to fetch'
];

function checkLoginAttempts(maxAttempts: number): { allowed: boolean; error?: string } {
  const attempts = parseInt(localStorage.getItem(ATTEMPT_KEY) || '0');
  const lockoutUntil = localStorage.getItem(LOCKOUT_KEY);

  if (lockoutUntil && new Date(lockoutUntil) > new Date()) {
    const timeLeft = Math.ceil((new Date(lockoutUntil).getTime() - Date.now()) / (60 * 1000));
    return { 
      allowed: false, 
      error: `Account locked. Please try again in ${timeLeft} minutes.` 
    };
  }

  if (lockoutUntil) {
    // Lockout period expired, reset attempts
    localStorage.removeItem(ATTEMPT_KEY);
    localStorage.removeItem(LOCKOUT_KEY);
    return { allowed: true };
  }

  return { allowed: attempts < maxAttempts };
}

function incrementLoginAttempts(maxAttempts: number) {
  const attempts = parseInt(localStorage.getItem(ATTEMPT_KEY) || '0') + 1;
  localStorage.setItem(ATTEMPT_KEY, attempts.toString());

  if (attempts >= maxAttempts) {
    // Set 15 minute lockout
    const lockoutUntil = new Date(Date.now() + LOCKOUT_DURATION).toISOString();
    localStorage.setItem(LOCKOUT_KEY, lockoutUntil);
    throw new Error('Account locked. Please try again in 15 minutes.');
  }
}

function resetLoginAttempts() {
  localStorage.removeItem(ATTEMPT_KEY);
  localStorage.removeItem(LOCKOUT_KEY);
}

async function attemptSignIn(email: string, password: string, retryCount = 0): Promise<AuthResponse> {
  try {
    // Add exponential backoff for retries
    if (retryCount > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount - 1)));
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`
      }
    });

    if (error) {
      // Only retry on network/database errors
      if ((error.status === 500 || 
           error.message?.toLowerCase().includes('network error') ||
           error.message?.toLowerCase().includes('failed to fetch') ||
           DB_ERROR_CODES.some(code => error.message?.toLowerCase().includes(code.toLowerCase()))) && 
          retryCount < MAX_RETRIES) {
        console.log(`Retrying auth attempt ${retryCount + 1}/${MAX_RETRIES}...`);
        return attemptSignIn(email, password, retryCount + 1);
      }
      throw error;
    }

    // Verify session after successful login
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Session verification failed');
    }

    return { data, error: null };
  } catch (error) {
    // Log the error for debugging
    console.error('Sign in attempt error:', {
      attempt: retryCount + 1,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    return { data: null, error };
  }
}

export async function signIn(email: string, password: string, maxAttempts: number = 10) {
  const { allowed, error: lockoutError } = checkLoginAttempts(maxAttempts);
  if (!allowed) {
    return { error: lockoutError || 'Too many login attempts. Please try again later.' };
  }

  // Validate inputs before attempting sign in
  const emailError = validateEmail(email);
  if (emailError) {
    incrementLoginAttempts(maxAttempts);
    return { error: emailError };
  }

  const passwordError = validatePassword(password);
  if (passwordError) {
    incrementLoginAttempts(maxAttempts);
    return { error: passwordError };
  }

  try {
    // Attempt sign in with retries
    const { data, error } = await attemptSignIn(email, password);

    if (error) {
      incrementLoginAttempts(maxAttempts);
      // Log the full error for debugging
      const errorMessage = getAuthErrorMessage(error);
      console.error('Sign in error:', { error, message: errorMessage });
      return { error: errorMessage };
    }
    
    // Ensure we have a valid session
    if (!data?.session?.access_token) {
      incrementLoginAttempts(maxAttempts);
      return { error: 'Unable to establish session. Please try again.' };
    }

    // Verify the session is valid
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      // Only increment attempts for actual auth failures, not network issues
      if (!sessionError.message?.toLowerCase().includes('network')) {
        incrementLoginAttempts(maxAttempts);
      }
      return { error: getAuthErrorMessage(sessionError) };
    }

    // Reset attempts on successful login
    resetLoginAttempts();
    return { user: data.user, session: data.session };
  } catch (error) {
    console.error('Sign in error:', error);
    incrementLoginAttempts(maxAttempts);
    return { error: 'Invalid email or password' };
  }
}